import { useState, useMemo, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DSBox from "../../system-design/DSBox";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSAlert from "../../system-design/DSAlert";
import useKlydoMutation from "../hooks/useKlydoMutation";
import { Klydo } from "../../../Types";
import { PublicationReviewStates } from "../../../api/klydo";
import useApprovePublicationMutation from "../KlydoReviewV2/hooks/useApprovePublicationMutation";
import DSButton from "../../system-design/DSButton";
import DSDateTimePicker from "../../system-design/DSDatePicker";

export enum PublishType {
  PUBLIC = "public",
}

interface PublishDateDialogProps {
  klydo: Klydo;
  open: boolean;
  onClose: () => void;
  type: PublishType;
}

/**
 * The fixed go-live moment for a FUTURE date: 15:00 UTC on the chosen day.
 *
 * ⚠️ Never call this for today. It predates the two-axis review model, when the
 * stored date was compared against `Date.now()` at every read site, so pinning
 * today to 15:00 UTC was harmless. It is not any more: the publication now runs
 * off a stored boolean gate plus an hourly cron, and 15:00 UTC put "today" up to
 * 18 hours in the FUTURE for anyone working before that hour — which turned
 * "publish now" into a scheduled publication that the cron then could not reach.
 * `resolveTimestamp` is what routes around it.
 */
const futureGoLiveTimestamp = (date: Date): number => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  const utcDate = new Date(Date.UTC(year, month, day, 15, 0, 0, 0));
  return Math.floor(utcDate.getTime());
};

const isToday = (date: Date) => {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

/**
 * The epoch ms to store for the picked day. Today means NOW, not 15:00 UTC — the
 * admin pressed "Publish", so the klydo has to be public when the call returns.
 */
const resolveTimestamp = (date: Date): number =>
  isToday(date) ? Date.now() : futureGoLiveTimestamp(date);

const PublishDateDialog = ({
  klydo,
  open,
  onClose,
}: PublishDateDialogProps) => {
  const requestedTimestamp =
    klydo.public?.reviewStatus?.requestPayload?.timestamp ?? null;
  // Chooses which of the two write paths handleSchedulePublish takes: change the
  // date on an existing grant, or create the grant along with the date.
  const hasApprovedRequest =
    klydo.public?.reviewStatus?.state === PublicationReviewStates.Approved;

  const initialDate = useMemo(() => {
    // The date that was asked for, not `public.status` — this dialog edits the
    // request, and a request may sit in the future without the klydo being public.
    return new Date(requestedTimestamp ?? new Date().setHours(0, 0, 0, 0));
  }, [requestedTimestamp, open]);

  const minDate = useMemo(() => new Date(), [open]);

  const [selectedDate, setSelectedDate] = useState(initialDate);

  const [status, setStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  useEffect(() => {
    setSelectedDate(initialDate);
  }, [initialDate]);

  useEffect(() => {
    if (open) {
      setShowSuccessAlert(false);
      setStatus({ type: null, message: "" });
    }
  }, [open]);

  const { mutateAsync: updateKlydo, isPending: isUpdatingKlydo } =
    useKlydoMutation(klydo.id);

  const { mutateAsync: approvePublication, isPending: isApprovingPublication } =
    useApprovePublicationMutation();

  const isSaving = isUpdatingKlydo || isApprovingPublication;

  useEffect(() => {
    if (showSuccessAlert) {
      const timer = setTimeout(() => {
        onClose();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [showSuccessAlert, onClose]);

  const handleSchedulePublish = async () => {
    setStatus({ type: null, message: "" });

    try {
      const publishDate = new Date(selectedDate);
      const timestamp = resolveTimestamp(publishDate);

      if (hasApprovedRequest) {
        // The grant already exists, so this is only a change of date. Store it and
        // open the gate only if that date has passed; a future date is left shut on
        // purpose — the api's hourly cron flips `status` when the date arrives.
        await updateKlydo({
          public: {
            status: timestamp <= Date.now(),
            reviewStatus: { requestPayload: { timestamp } },
          },
        });
      } else {
        // No approved request, which is the NORMAL state for a klydo sealed by its
        // collection: KlydoSealHandler deliberately writes no `public.*`, so there
        // is no request and never will be one. Writing a date on its own here is a
        // dead end — the hourly cron only looks at records whose state is
        // `approved`, so the klydo would sit at that date forever.
        //
        // So the admin's date IS the grant: approve, carrying the date, and the api
        // stamps `requestPayload.source: "admin"` so the record does not read as an
        // artist request nobody made. Approval opens the gate itself for a date
        // that has arrived, and leaves it shut for a future one so the cron
        // publishes it on the day.
        //
        // ⚠️ This also overrides a REJECTED request, replacing the rejection with
        // an approval. That is the intent — the admin is granting publication — but
        // it does discard the rejection message the artist was shown.
        await approvePublication({ klydoId: klydo.id, message: "", timestamp });
      }

      setStatus({
        type: "success",
        message: isToday(publishDate)
          ? "Successfully published"
          : `Successfully scheduled for ${publishDate.toLocaleDateString("en-GB")}`,
      });
      setShowSuccessAlert(true);
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error instanceof Error ? error.message : "Failed to schedule publish",
      });
    }
  };

  const handleCancel = () => {
    if (isSaving) return;
    onClose();
  };

  return (
    <DSDialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DSDialogTitle>
        Schedule Public Date
        <CloseIcon
          sx={{ float: "right", cursor: "pointer" }}
          onClick={handleCancel}
        />
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: 2,
          }}
        >
          <DSDateTimePicker
            selectedDate={selectedDate}
            onChange={setSelectedDate}
            minDate={minDate}
            isToday={isToday}
          />

          {!hasApprovedRequest && (
            <DSAlert severity="info">
              This Klydo has no approved publication request, so publishing it
              records an <strong>admin-scheduled</strong> grant rather than an
              artist request. It is marked as such wherever the publication date
              is shown.
            </DSAlert>
          )}

          {status.message && status.type && (
            <DSAlert severity={status.type}>{status.message}</DSAlert>
          )}
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSButton
          className="btn-simple"
          type="button"
          variant="text"
          onClick={handleCancel}
          sx={{ border: "1px solid red", color: "red" }}
        >
          Cancel
        </DSButton>

        <DSPrimaryButton onClick={handleSchedulePublish} disabled={isSaving}>
          {isSaving ? (
            <DSCircularProgress size={20} />
          ) : isToday(selectedDate) ? (
            "Publish"
          ) : (
            "Schedule Publish"
          )}
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default PublishDateDialog;
